// Browser-saved wishlist. Stores product handles in localStorage, so it survives
// reloads and return visits on the same device/browser (no account sync; that needs an app).
(() => {
  if (window.Wishlist) return;

  const KEY = 'wishlist:v1';
  const read = () => {
    try {
      const list = JSON.parse(localStorage.getItem(KEY));
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  };
  const write = (list) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) {
      // Private mode / storage blocked: the heart still toggles for this page view.
    }
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { list } }));
  };

  window.Wishlist = {
    all: read,
    has: (handle) => read().includes(handle),
    toggle(handle) {
      const list = read();
      const i = list.indexOf(handle);
      if (i === -1) list.unshift(handle);
      else list.splice(i, 1);
      write(list);
      return i === -1;
    },
  };

  customElements.define(
    'wishlist-button',
    class extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        this.sync = () => this.render(Wishlist.has(this.dataset.handle));
        this.button.addEventListener('click', (event) => {
          // Cards are one big link; keep the click on the heart.
          event.preventDefault();
          event.stopPropagation();
          this.render(Wishlist.toggle(this.dataset.handle));
        });
        document.addEventListener('wishlist:change', this.sync);
        window.addEventListener('storage', this.sync); // other tabs
        this.sync();
      }
      disconnectedCallback() {
        document.removeEventListener('wishlist:change', this.sync);
        window.removeEventListener('storage', this.sync);
      }
      render(saved) {
        this.button.setAttribute('aria-pressed', saved);
        this.button.setAttribute('aria-label', saved ? this.button.dataset.labelRemove : this.button.dataset.labelAdd);
      }
    }
  );

  // Wishlist page: renders each saved product with the theme's own card (templates/product.card.liquid).
  customElements.define(
    'wishlist-grid',
    class extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('ul');
        this.empty = this.querySelector('.wishlist__empty');
        this.load();
        document.addEventListener('wishlist:change', () => this.prune());
      }
      async load() {
        const handles = Wishlist.all();
        this.empty.hidden = handles.length > 0;
        const cards = await Promise.all(
          handles.map((handle) =>
            fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}?view=card`)
              .then((r) => (r.ok ? r.text() : ''))
              .catch(() => '')
          )
        );
        // Handles come from localStorage, so set them via the DOM rather than string-building HTML.
        // The card markup itself comes from our own store's server.
        this.list.replaceChildren(
          ...cards.flatMap((html, i) => {
            if (!html.trim()) return []; // product deleted or unpublished
            const li = document.createElement('li');
            li.className = 'grid__item';
            li.dataset.handle = handles[i];
            li.innerHTML = html;
            return [li];
          })
        );
        this.empty.hidden = this.list.children.length > 0;
      }
      // Un-hearting a card on this page removes it.
      prune() {
        const saved = Wishlist.all();
        this.list.querySelectorAll('li[data-handle]').forEach((li) => {
          if (!saved.includes(li.dataset.handle)) li.remove();
        });
        this.empty.hidden = this.list.children.length > 0;
      }
    }
  );
})();
